import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useI18n } from "@/i18n";
import { useAuth } from "@/auth";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, Users, Package, FileText, Settings as SettingsIcon,
  UsersRound, LogOut, Languages, Plane, Truck, ShieldCheck, BarChart3, Bell,
} from "lucide-react";

const NAV = [
  { to: "/", key: "dashboard", icon: LayoutDashboard, end: true },
  { to: "/clients", key: "clients", icon: Users },
  { to: "/programs", key: "programs", icon: Package },
  { to: "/bookings", key: "bookings", icon: FileText },
  { to: "/reminders", key: "paymentReminders", icon: Bell },
  { to: "/suppliers", key: "suppliers", icon: Truck },
  { to: "/reports", key: "reports", icon: BarChart3, roles: ["admin", "accountant"] },
  { to: "/team", key: "users", icon: UsersRound, adminOnly: true },
  { to: "/admin", key: "controlPanel", icon: ShieldCheck, adminOnly: true },
  { to: "/settings", key: "settings", icon: SettingsIcon, adminOnly: true },
];

export default function Layout() {
  const { t, lang, setLang } = useI18n();
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const doLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen flex bg-[hsl(var(--background))]" data-testid="app-layout">
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-64 border-e border-[hsl(var(--border))] bg-white/80 backdrop-blur-xl shrink-0">
        <div className="px-6 py-6 flex items-center gap-3 border-b border-[hsl(var(--border))]">
          <div className="w-10 h-10 rounded-xl bg-[#0A192F] text-white grid place-items-center">
            <Plane size={18} />
          </div>
          <div>
            <div className="display-font font-bold text-[hsl(var(--primary))]">{t("appName")}</div>
            <div className="text-[10px] tracking-widest uppercase text-[hsl(var(--muted-foreground))]">CRM</div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-6 space-y-1">
          {NAV.filter((n) => {
            if (n.adminOnly) return user?.role === "admin";
            if (n.roles) return n.roles.includes(user?.role);
            return true;
          }).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) => "sidebar-link " + (isActive ? "active" : "")}
              data-testid={`nav-${n.key}`}
            >
              <n.icon size={18} />
              <span>{t(n.key)}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-[hsl(var(--border))] space-y-2">
          <div className="px-2 pb-2">
            <div className="text-sm font-semibold text-[hsl(var(--primary))]">{user?.name}</div>
            <div className="text-xs text-[hsl(var(--muted-foreground))]">
              {user?.email} · {t(user?.role === "admin" ? "admin" : user?.role === "accountant" ? "accountant" : "sales")}
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start"
            onClick={() => setLang(lang === "ar" ? "en" : "ar")}
            data-testid="lang-toggle-btn"
          >
            <Languages size={16} className="me-2" />
            {lang === "ar" ? "English" : "العربية"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
            onClick={doLogout}
            data-testid="logout-btn"
          >
            <LogOut size={16} className="me-2" />
            {t("logout")}
          </Button>
        </div>
      </aside>

      {/* Mobile top-bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-30 bg-white/95 backdrop-blur border-b border-[hsl(var(--border))] flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#0A192F] text-white grid place-items-center">
            <Plane size={16} />
          </div>
          <div className="display-font font-bold">{t("appName")}</div>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => setLang(lang === "ar" ? "en" : "ar")}>
            <Languages size={16} />
          </Button>
          <Button variant="ghost" size="sm" onClick={doLogout}>
            <LogOut size={16} />
          </Button>
        </div>
      </div>

      {/* Mobile bottom nav */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-[hsl(var(--border))] flex justify-around py-2">
        {NAV.filter((n) => {
          if (n.adminOnly) return user?.role === "admin";
          if (n.roles) return n.roles.includes(user?.role);
          return true;
        }).slice(0, 5).map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            className={({ isActive }) =>
              "flex flex-col items-center gap-1 px-3 py-1 text-[10px] " +
              (isActive ? "text-[#0A192F] font-semibold" : "text-[hsl(var(--muted-foreground))]")
            }
          >
            <n.icon size={18} />
            <span>{t(n.key)}</span>
          </NavLink>
        ))}
      </div>

      {/* Main content */}
      <main className="flex-1 min-w-0 pt-16 md:pt-0 pb-20 md:pb-0">
        <div className="max-w-7xl mx-auto p-6 md:p-10 fade-up">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
